import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import FAQ from 'components/ui/FAQ';
import RelatedBlogs from 'components/RelatedBlogs';
import { n8nAutomationFAQs } from '../data/faqs';
import { useNavigate } from 'react-router-dom';

const N8nAutomation = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'n8n Automation Services - Self-Hosted Workflow Automation | OctalChip',
    description: 'Professional n8n automation services. We help businesses design and deploy powerful self-hosted automation workflows using n8n. Ideal for internal tools, backend systems, and secure enterprise workflows.',
    keywords: 'n8n automation, n8n workflow automation, self-hosted automation, n8n integration, n8n development, workflow automation services, n8n setup, n8n consulting, business process automation',
    canonical: 'https://www.octalchip.com/services/n8n-automation',
    faqData: n8nAutomationFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "n8n Automation Services",
      "description": "Self-hosted workflow automation using n8n for secure and scalable business process automation",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const whatWeDo = [
    {
      title: 'Self-hosted n8n setup',
      description: 'Deploy n8n on AWS, Docker, or your preferred infrastructure with proper security and scaling configurations',
      icon: 'Server',
      color: 'from-[#00d690] to-[#00b876]'
    },
    {
      title: 'Custom workflow development',
      description: 'Build complex workflows tailored to your business processes with error handling and monitoring',
      icon: 'Code',
      color: 'from-[#00b876] to-[#00d690]'
    },
    {
      title: 'Webhook-based automation',
      description: 'Create webhook endpoints and integrations for real-time event-driven automation',
      icon: 'Link',
      color: 'from-[#00d690] to-[#00b876]'
    },
    {
      title: 'API integrations',
      description: 'Connect n8n with REST APIs, GraphQL, and third-party services for seamless data flow',
      icon: 'Link',
      color: 'from-[#00b876] to-[#00d690]'
    },
    {
      title: 'Scheduled jobs & background processing',
      description: 'Set up cron jobs, scheduled tasks, and background processing for automated operations',
      icon: 'Clock',
      color: 'from-[#00d690] to-[#00b876]'
    },
    {
      title: 'Security and access control',
      description: 'Implement authentication, authorization, and security best practices for enterprise deployments',
      icon: 'Shield',
      color: 'from-[#00b876] to-[#00d690]'
    }
  ];

  const useCases = [
    {
      title: 'Website forms → Database → Email/Slack',
      description: 'Automatically capture form submissions, store in database, and notify teams via email or Slack',
      icon: 'FileText'
    },
    {
      title: 'CRM sync automation',
      description: 'Keep your CRM synchronized with other systems, ensuring data consistency across platforms',
      icon: 'Users'
    },
    {
      title: 'AI-powered data processing',
      description: 'Process data through AI APIs, transform results, and route to appropriate systems',
      icon: 'Brain'
    },
    {
      title: 'Internal reporting pipelines',
      description: 'Automate data collection, transformation, and report generation for internal dashboards',
      icon: 'BarChart'
    },
    {
      title: 'Backend task automation',
      description: 'Automate backend processes like data cleanup, batch operations, and system maintenance',
      icon: 'Settings'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section - Split Design */}
      <section id="hero" className="relative py-12 md:py-20 lg:py-28 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-[#00d690]/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#00b876]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* Diagonal Lines */}
        <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00d690]/20 to-transparent transform rotate-45"></div>
        <div className="absolute bottom-1/4 right-0 w-full h-px bg-gradient-to-l from-transparent via-[#00b876]/20 to-transparent transform -rotate-45"></div>

        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-5 gap-8 items-center">
              {/* Left - Icon & Badge */}
              <div className="md:col-span-2 space-y-6">
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-[#00d690]/10 rounded-full border border-[#00d690]/20">
                  <div className="w-2 h-2 bg-[#00d690] rounded-full animate-pulse"></div>
                  <span className="text-sm font-semibold text-[#00d690]">Self-Hosted Automation</span>
                </div>
                
                <div className="relative">
                  <div className="w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-3xl shadow-2xl flex items-center justify-center transform rotate-6 hover:rotate-12 transition-transform duration-300">
                    <Icon name="Settings" size={64} className="text-white" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#00b876]/20 rounded-2xl transform -rotate-12"></div>
                </div>
              </div>

              {/* Right - Content */}
              <div className="md:col-span-3 space-y-6">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gray-800">Self-Hosted &</span>
                  <br />
                  <span className="bg-gradient-to-r from-[#00d690] to-[#00b876] bg-clip-text text-transparent">
                    Custom Automation
                  </span>
                  <br />
                  <span className="text-gray-800 text-3xl sm:text-4xl md:text-5xl">with n8n</span>
                </h1>
                
                <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                  We help businesses design and deploy powerful self-hosted automation workflows using n8n. 
                  Ideal for internal tools, backend systems, and secure enterprise workflows.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <Button 
                    variant="primary" 
                    onClick={() => navigate('/contact#contact-form')}
                    className="px-8 py-4 text-base shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                  >
                    Talk to Automation Expert
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/services/workflow-automation#what-we-offer')}
                    className="px-8 py-4 text-base border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                  >
                    View All Automation Services
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section - Zigzag Layout */}
      <section id="what-we-do" className="py-16 md:py-24 relative">
        <div className="absolute inset-0 bg-gray-50"></div>
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-br from-white to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              What We Do with n8n
            </h2>
            <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive n8n services to build and deploy your automation workflows
            </p>
          </div>
          
          <div className="max-w-7xl mx-auto space-y-6">
            {whatWeDo.map((item, index) => (
              <div 
                key={index} 
                className={`flex flex-col md:flex-row gap-6 items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Icon Side */}
                <div className={`flex-shrink-0 w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gradient-to-br ${item.color} shadow-xl flex items-center justify-center transform ${
                  index % 2 === 0 ? 'rotate-6 hover:rotate-12' : '-rotate-6 hover:-rotate-12'
                } transition-transform duration-300`}>
                  <Icon name={item.icon} size={48} className="text-white" />
                </div>
                
                {/* Content Side */}
                <div className={`flex-1 bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-l-4 ${
                  index % 2 === 0 ? 'border-[#00d690]' : 'border-[#00b876]'
                }`}>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section - Card Grid with Accents */}
      <section id="use-cases" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-white"></div>
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-gray-50 to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              n8n Use Cases
            </h2>
            <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Real-world automation scenarios we've built with n8n
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="group relative bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-[#00d690]/30"
              >
                {/* Corner Accent */}
                <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-br ${
                  index % 3 === 0 ? 'from-[#00d690] to-[#00b876]' :
                  index % 3 === 1 ? 'from-[#00b876] to-[#00d690]' :
                  'from-[#00d690] via-[#00b876] to-[#00d690]'
                } rounded-bl-3xl rounded-tr-2xl opacity-20 group-hover:opacity-100 transition-opacity`}></div>
                
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-[#00d690]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#00d690] transition-all duration-300">
                    <Icon name={useCase.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {useCase.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white"></div>
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-white to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl text-center relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#00d690] rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#00b876] rounded-full blur-3xl"></div>
              </div>
              
              <div className="relative z-10">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                  Build Secure & Scalable Automation with n8n
                </h2>
                <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
                <p className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                  Ready to deploy self-hosted automation workflows? Let's discuss your requirements and build 
                  secure, scalable automation solutions with n8n.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/contact#contact-form')}
                    className="bg-gradient-to-r from-[#00d690] to-[#00b876] hover:from-[#00b876] hover:to-[#00d690] text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    Talk to Automation Expert
                  </button>
                  <button
                    onClick={() => navigate('/services/workflow-automation#what-we-offer')}
                    className="border-2 border-white/30 text-white hover:bg-white/10 font-bold py-4 px-8 rounded-xl transition-all duration-300 backdrop-blur-sm"
                  >
                    View All Automation Services
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={n8nAutomationFAQs}
        title="n8n Automation FAQs"
        description="Common questions about n8n automation, self-hosted workflows, and enterprise automation."
      />

      {/* Related Blogs Section */}
      <RelatedBlogs
        servicePath="/services/n8n-automation"
        serviceTags={['n8n', 'Workflow Automation', 'Automation', 'Self-hosted', 'n8n Automation', 'Custom Development']}
        serviceName="n8n Automation"
        limit={6}
        title="n8n Automation Related Blogs"
      />

      <Footer />
    </div>
  );
};

export default N8nAutomation;
